import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Badge,
  Tooltip,
  Button,
  Checkbox
} from '@mui/material';
import {
  History as HistoryIcon,
  AccessTime as TimeIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { FilterState } from '../hooks/useFilterHistory';

interface RecentFiltersMenuProps {
  recentFilters: FilterState[];
  currentFilters: Record<string, any>;
  onApplyFilter: (filterId: string) => void;
  onClearHistory?: () => void;
  onDeleteFilters?: (filterIds: string[]) => void;
  disabled?: boolean;
}

/**
 * Component to display and manage recent filter states
 * Shows last 10 filter configurations with timestamps
 */
export const RecentFiltersMenu: React.FC<RecentFiltersMenuProps> = ({
  recentFilters,
  currentFilters,
  onApplyFilter,
  onClearHistory,
  onDeleteFilters,
  disabled = false
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
    setSelectedIds(new Set()); // Clear selection when closing
  };
  
  const handleApplyFilter = (filterId: string) => {
    onApplyFilter(filterId);
    handleClose();
  };
  
  const handleDelete = () => {
    if (selectedIds.size > 0 && onDeleteFilters) {
      // Delete selected items
      onDeleteFilters(Array.from(selectedIds));
      setSelectedIds(new Set());
    } else if (onClearHistory) {
      // Delete all items
      onClearHistory();
    }
    handleClose();
  };
  
  const handleCheckboxToggle = (filterId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(filterId)) {
        newSet.delete(filterId);
      } else {
        newSet.add(filterId);
      }
      return newSet;
    });
  };
  
  const open = Boolean(anchorEl);
  
  // Check if a filter state matches current filters
  const isCurrentState = (filterState: FilterState): boolean => {
    const keys1 = Object.keys(filterState.filters);
    const keys2 = Object.keys(currentFilters);
    
    if (keys1.length !== keys2.length) return false;
    
    return keys1.every(key => {
      const val1 = filterState.filters[key];
      const val2 = currentFilters[key];
      
      // Simple equality check (could be enhanced with deep equality)
      if (Array.isArray(val1) && Array.isArray(val2)) {
        return JSON.stringify(val1) === JSON.stringify(val2);
      }
      if (typeof val1 === 'object' && typeof val2 === 'object') {
        return JSON.stringify(val1) === JSON.stringify(val2);
      }
      return val1 === val2;
    });
  };
  
  // Format relative time (e.g., "2 minutes ago", "1 hour ago")
  const formatRelativeTime = (timestamp: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - timestamp.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    // For older dates, show full date
    return timestamp.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: timestamp.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };
  
  // Get count of recent filters (excluding current state)
  const filterCount = recentFilters.filter(f => !isCurrentState(f)).length;
  
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title="Recent Filters">
          <IconButton
            onClick={handleClick}
            disabled={disabled || recentFilters.length === 0}
            size="small"
            sx={{
              color: '#71717a',
              '&:hover': {
                color: '#18181b',
                backgroundColor: '#f4f4f5'
              },
              '&:disabled': {
                color: '#d4d4d8'
              }
            }}
          >
            <Badge 
              badgeContent={filterCount} 
              color="primary"
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#f97316',
                  color: '#ffffff',
                  fontSize: '10px',
                  height: '16px',
                  minWidth: '16px',
                  padding: '0 4px'
                }
              }}
            >
              <HistoryIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        <Tooltip 
          title="This function will list, reapply and delete existing filters."
          arrow
        >
          <InfoIcon 
            sx={{ 
              fontSize: '16px', 
              color: '#71717a',
              cursor: 'help'
            }} 
          />
        </Tooltip>
      </Box>
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPopover-paper': {
            mt: 1,
            minWidth: 400,
            maxWidth: 500,
            bgcolor: '#ffffff',
            border: '1px solid #e4e4e7',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }
        }}
      >
        <Box>
          {/* Header */}
          <Box sx={{ 
            p: 2, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            borderBottom: '1px solid #e4e4e7'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon sx={{ color: '#18181b', fontSize: '20px' }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#18181b',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '24px'
                }}
              >
                Recent Filters
              </Typography>
            </Box>
            {((onClearHistory && recentFilters.length > 0) || (onDeleteFilters && selectedIds.size > 0)) && (
              <Tooltip 
                title={selectedIds.size > 0 
                  ? `Delete ${selectedIds.size} selected ${selectedIds.size === 1 ? 'item' : 'items'}` 
                  : 'Clear all history'
                }
              >
                <IconButton
                  size="small"
                  onClick={handleDelete}
                  sx={{
                    color: '#71717a',
                    '&:hover': {
                      color: '#ef4444',
                      backgroundColor: '#fef2f2'
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          {/* Filter List */}
          {recentFilters.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#71717a',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px'
                }}
              >
                No recent filter states
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0, maxHeight: '400px', overflowY: 'auto' }}>
              {recentFilters.map((filterState, index) => {
                const isCurrent = isCurrentState(filterState);
                const isSelected = selectedIds.has(filterState.id);
                
                return (
                  <React.Fragment key={filterState.id}>
                    {index > 0 && <Divider />}
                    <ListItem 
                      disablePadding
                      sx={{
                        backgroundColor: isCurrent ? '#f0fdf4' : isSelected ? '#fef3c7' : 'transparent',
                        '&:hover': {
                          backgroundColor: isCurrent ? '#f0fdf4' : isSelected ? '#fef3c7' : '#fafafa'
                        }
                      }}
                    >
                      {/* Checkbox */}
                      <Checkbox
                        checked={isSelected}
                        onClick={(e) => handleCheckboxToggle(filterState.id, e)}
                        sx={{
                          ml: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      />
                      
                      <ListItemButton
                        onClick={() => handleApplyFilter(filterState.id)}
                        disabled={isCurrent}
                        sx={{
                          py: 1.5,
                          px: 1,
                          pr: 2,
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          flex: 1
                        }}
                      >
                        {/* Time Icon */}
                        <TimeIcon 
                          sx={{ 
                            color: isCurrent ? '#22c55e' : '#71717a', 
                            fontSize: '18px',
                            mt: 0.3,
                            flexShrink: 0
                          }} 
                        />
                        
                        {/* Content */}
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#18181b',
                                  fontFamily: "'Inter', sans-serif",
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                  flex: 1
                                }}
                              >
                                {filterState.summary}
                              </Typography>
                              {isCurrent && (
                                <CheckIcon 
                                  sx={{ 
                                    color: '#22c55e', 
                                    fontSize: '18px',
                                    flexShrink: 0
                                  }} 
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#71717a',
                                fontFamily: "'Inter', sans-serif",
                                fontSize: '12px',
                                lineHeight: '16px',
                                mt: 0.5,
                                display: 'block'
                              }}
                            >
                              {formatRelativeTime(filterState.timestamp)}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          )}
          
          {/* Footer Info */}
          {recentFilters.length > 0 && (
            <Box 
              sx={{ 
                p: 2, 
                borderTop: '1px solid #e4e4e7',
                backgroundColor: '#fafafa'
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#71717a',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '11px',
                  lineHeight: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <TimeIcon sx={{ fontSize: '14px' }} />
                Showing last {recentFilters.length} filter state{recentFilters.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

